import { z } from "zod";
import { DbCreateInputOmit, DbUpdate, nullishToOptional } from "../../helpers";
import { OmitStrict } from "../../utils";
import {
  DesignElementBase,
  DesignElementBrandedPart,
  DesignElementGroupableRequiredPart,
  DesignElementOrigin,
} from "./base";
import { FigmaComponentPropertyMap } from "./component-properties";

//
// Figma Component
//

export const FigmaComponentOriginPart = z.object({
  nodeId: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
});

const FigmaComponentAsset = z.object({
  assetId: z.string(),
  assetPath: z.string(),
});

export const FigmaComponentOrigin = DesignElementOrigin.extend(FigmaComponentOriginPart.shape);

export const FigmaComponent = DesignElementBase.extend(DesignElementGroupableRequiredPart.shape)
  .extend(DesignElementBrandedPart.shape)
  .extend({
    origin: FigmaComponentOrigin.optional(),
    thumbnail: FigmaComponentAsset,
    componentPropertyDefinitions: FigmaComponentPropertyMap.optional(),
    variantPropertyValues: z.record(z.string()).optional(),

    svg: FigmaComponentAsset.optional(),
    isAsset: z.boolean(),
    parentComponentPersistentId: nullishToOptional(z.string()),
    renderNodeId: z.string().optional(),
  });

export type FigmaComponentAsset = z.infer<typeof FigmaComponentAsset>;
export type FigmaComponent = z.infer<typeof FigmaComponent>;
export type FigmaComponentOrigin = z.infer<typeof FigmaComponentOrigin>;
export type ImportedFigmaComponent = OmitStrict<FigmaComponent, "origin"> & { origin: FigmaComponentOrigin };

//
// Support types
//

export type CreateFigmaComponent = DbCreateInputOmit<FigmaComponent>;
export type UpdateFigmaComponent = OmitStrict<DbUpdate<FigmaComponent>, "brandPersistentId" | "designSystemVersionId">;

export type FigmaComponentDiff = {
  toCreate: CreateFigmaComponent[];
  toUpdate: UpdateFigmaComponent[];
  toDelete: FigmaComponent["id"][];
};

//
// Utils
//

export function isImportedFigmaComponent(component: FigmaComponent): component is ImportedFigmaComponent {
  return !!component.origin;
}
